import React, { useState } from 'react';
import { X, Heart } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface MatchingLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (user: any) => void;
}

const MatchingLoginModal: React.FC<MatchingLoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: contacts, error: contactError } = await supabase
        .from('contacts')
        .select('id, name, email, phone, photo_url')
        .ilike('email', email.trim());

      if (contactError) throw contactError;

      const contact = contacts?.find(c => c.phone === phone.trim());

      if (!contact) {
        setError('Credenziali non valide. Verifica email e telefono.');
        setLoading(false);
        return;
      }

      const { data: participantData, error: participantError } = await supabase
        .from('event_participants')
        .select('id')
        .eq('participant_id', contact.id)
        .limit(1);

      if (participantError) throw participantError;

      if (!participantData || participantData.length === 0) {
        setError('Non sei ancora iscritto a nessun evento. Contatta l\'organizzatore per essere aggiunto.');
        setLoading(false);
        return;
      }

      localStorage.setItem('heartlink_user', JSON.stringify(contact));
      onLoginSuccess(contact);
    } catch (err: any) {
      setError(err.message || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-pink-500 to-rose-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Heart className="h-8 w-8 fill-white" />
              <div>
                <h2 className="text-2xl font-bold">HeartLink</h2>
                <p className="text-white/90 text-sm">Trova il tuo match perfetto</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="la tua email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefono
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="il tuo numero"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 bg-gradient-to-r from-pink-500 to-rose-600 text-white py-3 rounded-lg font-semibold hover:from-pink-600 hover:to-rose-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Caricamento...' : 'Accedi'}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Usa la tua email e telefono registrati nel nostro sistema per accedere.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatchingLoginModal;
